import {EntityMinMaxConfig} from 'Quoting/models/DTOs/QuoteForm';
import {getByKeyPath, isDefined} from 'Quoting/utils/utilities';
import {List as IList, Map as IMap} from 'immutable';
import {FieldValidator} from '../../products/types';
import {
  CONRACTED_COMPENSATION_FIELD_ID,
  GPLU_CHILD_TERM_LIFE_FIELD_ID,
  GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID,
  GPLU_WHOLE_LIFE_FIELD_ID,
  GPLU_SPOUSE_TYPE,
  GPLUCoverageCode,
  MISSING_PLAN_ERROR_KEY,
} from './gpluConstants';
import {buildValidationResult, createMinMaxValidatorsByProduct} from 'Quoting/utils/validationHelpers';
import {A360ProductModel} from 'Quoting/models/Quoting';

const GPLU_COVERAGE_FIELD_VALIDATORS: {
  [key: string]: FieldValidator[];
} = {
  WholeLife: [
    {
      validate: (coverage: IMap<string, any>) => {
        const isValid = (getByKeyPath(coverage, 'coverageGroupsLinks') as IList<any>)?.size > 0;
        return buildValidationResult(
          GPLU_WHOLE_LIFE_FIELD_ID.censusClassCoverageGroupsLinks,
          isValid,
          "At least one 'Classification Group' should be added to the quote"
        );
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const isValid = isDefined(getByKeyPath(coverage, 'fundingStructure.payrollFrequencyCd'));
        return buildValidationResult(
          GPLU_WHOLE_LIFE_FIELD_ID.payrollFrequencyCd,
          isValid,
          "'Payroll Frequency' under Participant Whole Life is mandatory"
        );
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const isValid = isDefined(getByKeyPath(coverage, 'ratingDetails.rateFormatCd'));
        return buildValidationResult(
          GPLU_WHOLE_LIFE_FIELD_ID.rateFormatCd,
          isValid,
          "'Rate Format' under Participant Whole Life is mandatory"
        );
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
          const isValid =
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount') >=
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMaxAmount.amount');

          return buildValidationResult(
            GPLU_WHOLE_LIFE_FIELD_ID.benefitMaxAmount,
            isValid,
            'Maximum Benefit Amount under Participant Whole Life cannot exceed the GI Amount'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
          const isValid =
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount') >=
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMinAmount.amount');

          return buildValidationResult(
            GPLU_WHOLE_LIFE_FIELD_ID.benefitMinAmount,
            isValid,
            'Minimum Benefit Amount under Participant Whole Life cannot exceed the GI Amount'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
          const isValid =
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount') >=
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.incrementAmount.amount');

          return buildValidationResult(
            GPLU_WHOLE_LIFE_FIELD_ID.incrementAmount,
            isValid,
            'Increment Amount under Participant Whole Life cannot exceed the GI Amount'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
          const isValid =
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMaxAmount.amount') >
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMinAmount.amount');

          return buildValidationResult(
            GPLU_WHOLE_LIFE_FIELD_ID.benefitMaxAmount,
            isValid,
            'Maximum Benefit Amount under Participant Whole Life must be greater than Minimum Benefit Amount'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.singleValue'))) {
          const isValid =
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount') >=
            getByKeyPath(coverage, 'benefitSchedule.singleValue.flatBenefitAmount.amount');

          return buildValidationResult(
            GPLU_WHOLE_LIFE_FIELD_ID.flatBenefitAmount,
            isValid,
            'Flat Benefit Amount under Participant Whole Life cannot exceed the GI Amount'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const multipleValues = getByKeyPath(coverage, 'benefitSchedule.multipleValues') as IList<any>;
        if (isDefined(multipleValues) && multipleValues?.size > 0) {
          const giAmount = getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount');
          const isValid = multipleValues?.every(v => v?.get('specifiedAmount') <= giAmount);

          return buildValidationResult(
            GPLU_WHOLE_LIFE_FIELD_ID.specifiedBenefitAmount,
            isValid,
            'Benefit amount(s) under Participant Whole Life cannot exceed underwriting limits'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const isMultipleValues =
          (getByKeyPath(coverage, 'benefitSchedule.benefitTypeCd') as string) === 'MultipleValues';

        const multipleValues = getByKeyPath(coverage, 'benefitSchedule.multipleValues') as IList<any>;
        const isEmpty = isMultipleValues && isDefined(multipleValues) && multipleValues?.size === 0;

        return buildValidationResult(
          GPLU_WHOLE_LIFE_FIELD_ID.specifiedBenefitAmount,
          !isEmpty,
          "'Specified Benefit Amounts' is mandatory under Participant Whole Life"
        );
      },
    },
  ],
  SpouseWholeLife: [
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.WS,
          'rangeValues'
        );
        

        if (isDefined(spouseUwAmount) && benefitTypeValue) {
          const wsBenefitMaxAmount =
            getByKeyPath(benefitTypeValue, 'benefitMaxAmount.amount') ??
            benefitTypeValue?.benefitMaxAmount?.get('amount');

          const isValid = spouseUwAmount >= wsBenefitMaxAmount;

          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.benefitMaxAmount,
            isValid,
            'Working Spouse > Maximum Benefit Amount cannot exceed the GI Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.WS,
          'rangeValues'
        );

        if (isDefined(spouseUwAmount) && benefitTypeValue) {
          const wsBenefitMinAmount =
            getByKeyPath(benefitTypeValue, 'benefitMinAmount.amount') ??
            benefitTypeValue?.benefitMinAmount?.get('amount');

          const isValid = spouseUwAmount >= wsBenefitMinAmount;

          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.benefitMinAmount,
            isValid,
            'Working Spouse > Minimum Benefit Amount cannot exceed the GI Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.WS,
          'rangeValues'
        );

        if (isDefined(spouseUwAmount) && benefitTypeValue) {
          const wsIncrementAmount =
            getByKeyPath(benefitTypeValue, 'incrementAmount.amount') ??
            benefitTypeValue?.incrementAmount?.get('amount');

          const isValid = spouseUwAmount >= wsIncrementAmount;

          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.incrementAmount,
            isValid,
            'Working Spouse > Increment Amount cannot exceed the GI Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(coverage, GPLU_SPOUSE_TYPE.WS, 'rangeValues');
        if (benefitTypeValue) {
          const wsBenefitMaxAmount =
            getByKeyPath(benefitTypeValue, 'benefitMaxAmount.amount') ??
            benefitTypeValue?.benefitMaxAmount?.get('amount');

          const wsBenefitMinAmount =
            getByKeyPath(benefitTypeValue, 'benefitMinAmount.amount') ??
            benefitTypeValue?.benefitMinAmount?.get('amount');

          const isValid = wsBenefitMaxAmount > wsBenefitMinAmount;

          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.benefitMaxAmount,
            isValid,
            'Working Spouse > Maximum Benefit Amount must be greater than Minimum Benefit Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.WS,
          'singleValue'
        );
        if (isDefined(spouseUwAmount) && benefitTypeValue) {
          const flatBenefitAmount =
            (getByKeyPath(benefitTypeValue, 'flatBenefitAmount.amount') as number) ??
            (benefitTypeValue?.flatBenefitAmount?.amount as number);

          const isValid = spouseUwAmount >= flatBenefitAmount;

          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.flatBenefitAmount,
            isValid,
            'Working Spouse > Flat Benefit Amount cannot exceed the GI Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.WS,
          'multipleValues'
        );

        if (isDefined(spouseUwAmount) && isDefined(benefitTypeValue) && benefitTypeValue?.size > 0) {
          const isValid = benefitTypeValue?.every(v => v?.get('specifiedAmount') <= spouseUwAmount);

          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.specifiedBenefitAmount,
            isValid,
            'Working Spouse > Benefit amount(s) under Spouse Whole Life cannot exceed underwriting limits'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {benefitScheduleType, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.WS,
          'multipleValues'
        );

        const isMultipleValues = (benefitScheduleType?.get('benefitTypeWsCd') as string) === 'MultipleValues';

        const isEmpty = isMultipleValues && IList.isList(benefitTypeValue) && benefitTypeValue?.size === 0;

        return buildValidationResult(
          GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.specifiedBenefitAmount,
          !isEmpty,
          "Working Spouse > 'Specified Benefit Amounts' is mandatory under Spouse Whole Life"
        );
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.NWS,
          'rangeValues'
        );

        if (isDefined(spouseUwAmount) && benefitTypeValue) {
          const nwsBenefitMaxAmount =
            getByKeyPath(benefitTypeValue, 'benefitMaxAmount.amount') ??
            benefitTypeValue?.benefitMaxAmount?.get('amount');

          const isValid = spouseUwAmount >= nwsBenefitMaxAmount;
          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.benefitMaxAmount,
            isValid,
            'Non-Working Spouse > Maximum Benefit Amount cannot exceed the GI Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.NWS,
          'rangeValues'
        );

        if(isDefined(spouseUwAmount) && benefitTypeValue) {
          const nwsBenefitMinAmount =
            getByKeyPath(benefitTypeValue, 'benefitMinAmount.amount') ??
            benefitTypeValue?.benefitMinAmount?.get('amount');

          const isValid = spouseUwAmount >= nwsBenefitMinAmount;
          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.benefitMinAmount,
            isValid,
            'Non-Working Spouse > Minimum Benefit Amount cannot exceed the GI Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.NWS,
          'rangeValues'
        );

        if(isDefined(spouseUwAmount) && benefitTypeValue) {
          const nwsIncrementAmount =
            getByKeyPath(benefitTypeValue, 'incrementAmount.amount') ??
            benefitTypeValue?.incrementAmount?.get('amount');

          const isValid = spouseUwAmount >= nwsIncrementAmount;
          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.incrementAmount,
            isValid,
            'Non-Working Spouse > Increment Amount cannot exceed the GI Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.NWS,
          'rangeValues'
        );

        if (benefitTypeValue) {
          const nwsBenefitMaxAmount =
            getByKeyPath(benefitTypeValue, 'benefitMaxAmount.amount') ??
            benefitTypeValue?.benefitMaxAmount?.get('amount');

          const nwsBenefitMinAmount =
            getByKeyPath(benefitTypeValue, 'benefitMinAmount.amount') ??
            benefitTypeValue?.benefitMinAmount?.get('amount');

          const isValid = nwsBenefitMaxAmount > nwsBenefitMinAmount;
          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.benefitMaxAmount,
            isValid,
            'Non-Working Spouse > Maximum Benefit Amount must be greater than Minimum Benefit Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.NWS,
          'singleValue'
        );

        if(isDefined(spouseUwAmount) && benefitTypeValue) {
          const flatBenefitAmount =
            getByKeyPath(benefitTypeValue, 'flatBenefitAmount.amount') ?? benefitTypeValue?.flatBenefitAmount?.amount;

          const isValid = spouseUwAmount >= flatBenefitAmount;
          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.flatBenefitAmount,
            isValid,
            'Non-Working Spouse > Flat Benefit Amount cannot exceed the GI Amount under Spouse Whole Life'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {spouseUwAmount, benefitScheduleType, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.NWS,
          'multipleValues'
        );

        const isMultipleValues = (benefitScheduleType?.get('benefitTypeNwsCd') as string) === 'MultipleValues';

        if(isDefined(spouseUwAmount) && isMultipleValues && isDefined(benefitTypeValue) && benefitTypeValue?.size > 0) {
          const isValid = benefitTypeValue?.every(v => v?.get('specifiedAmount') <= spouseUwAmount);
          return buildValidationResult(
            GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.specifiedBenefitAmount,
            isValid,
            'Non-Working Spouse > Benefit amount(s) under Spouse Whole Life cannot exceed underwriting limits'
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const {benefitScheduleType, benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
          coverage,
          GPLU_SPOUSE_TYPE.NWS,
          'multipleValues'
        );

        const isMultipleValues = (benefitScheduleType?.get('benefitTypeNwsCd') as string) === 'MultipleValues';

        const isEmpty =
          (isMultipleValues && IList.isList(benefitTypeValue) && benefitTypeValue?.size === 0) ||
          (isMultipleValues && benefitTypeValue?.length === 0);

        return buildValidationResult(
          GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.specifiedBenefitAmount,
          !isEmpty,
          "Non-Working Spouse > 'Specified Benefit Amounts' is mandatory under Spouse Whole Life"
        );
      },
    },
  ],
  ChildTermLife: [
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
          const isValid =
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount') >=
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMaxAmount.amount');

          return buildValidationResult(
            GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitMaxAmount,
            isValid,
            "Maximum Benefit Amount under Children's Term Rider cannot exceed the GI Amount"
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
          const isValid =
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount') >=
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMinAmount.amount');

          return buildValidationResult(
            GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitMinAmount,
            isValid,
            "Minimum Benefit Amount under Children's Term Rider cannot exceed the GI Amount"
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
          const isValid =
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount') >=
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.incrementAmount.amount');

          return buildValidationResult(
            GPLU_CHILD_TERM_LIFE_FIELD_ID.incrementAmount,
            isValid,
            "Increment Amount under Children's Term Rider cannot exceed the GI Amount"
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
          const isValid =
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMaxAmount.amount') >
            getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMinAmount.amount');

          return buildValidationResult(
            GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitMaxAmount,
            isValid,
            "Maximum Benefit Amount under Children's Term Rider must be greater than Minimum Benefit Amount"
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        if (isDefined(getByKeyPath(coverage, 'benefitSchedule.singleValue'))) {
          const isValid =
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount') >=
            getByKeyPath(coverage, 'benefitSchedule.singleValue.flatBenefitAmount.amount');

          return buildValidationResult(
            GPLU_CHILD_TERM_LIFE_FIELD_ID.flatBenefitAmount,
            isValid,
            "Flat Benefit Amount under Children's Term Rider cannot exceed the GI Amount"
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const multipleValues = getByKeyPath(coverage, 'benefitSchedule.multipleValues') as IList<any>;
        if (isDefined(multipleValues) && multipleValues?.size > 0) {
          const giAmount = getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount');
          const isValid = multipleValues?.every(v => v?.get('specifiedAmount') <= giAmount);

          return buildValidationResult(
            GPLU_CHILD_TERM_LIFE_FIELD_ID.specifiedBenefitAmount,
            isValid,
            "Benefit amount(s) under Children's Term Rider cannot exceed underwriting limits"
          );
        }
      },
    },
    {
      validate: (coverage: IMap<string, any>) => {
        const isMultipleValues =
          (getByKeyPath(coverage, 'benefitSchedule.benefitTypeCd') as string) === 'MultipleValues';

        const multipleValues = getByKeyPath(coverage, 'benefitSchedule.multipleValues') as IList<any>;

        const isEmpty = isMultipleValues && isDefined(multipleValues) && multipleValues?.size === 0;

        return buildValidationResult(
          GPLU_CHILD_TERM_LIFE_FIELD_ID.specifiedBenefitAmount,
          !isEmpty,
          "'Specified Benefit Amounts' is mandatory under Children's Term Rider"
        );
      },
    },
  ],
};

const GPLU_QUOTE_FIELDS_VALIDATORS: FieldValidator[] = [
  {
    validate: (quote: IMap<string, any>) => {
      const isValid = (getByKeyPath(quote, 'benefitPackagingDetail.plans') as IList<any>)?.size > 0;
      return buildValidationResult(
        MISSING_PLAN_ERROR_KEY,
        isValid,
        "At least one 'Plan' should be added to the quote."
      );
    },
  },
  {
    validate: (quote: IMap<string, any>) => {
      const isValid = isDefined(getByKeyPath(quote, 'quotedEnrollmentType'));
      return buildValidationResult(
        CONRACTED_COMPENSATION_FIELD_ID.quotedEnrollmentType,
        isValid,
        "Producers & Compensation: 'Quoted Enrollment Type' is mandatory"
      );
    },
  },
  {
    validate: (quote: IMap<string, any>) => {
      const isValid = isDefined(getByKeyPath(quote, 'quotedCommissionSeries'));
      return buildValidationResult(
        CONRACTED_COMPENSATION_FIELD_ID.quotedCommissionSeries,
        isValid,
        "Producers & Compensation: 'Quoted Commission Series' is mandatory"
      );
    },
  },
  {
    validate: (quote: IMap<string, any>) => {
      const isValid = isDefined(getByKeyPath(quote, 'quotedCommissionLevel'));
      return buildValidationResult(
        CONRACTED_COMPENSATION_FIELD_ID.quotedCommissionLevel,
        isValid,
        "Producers & Compensation: 'Quoted Commission Level' is mandatory"
      );
    },
  },
];

const GPLU_PLAN_FIELD_VALIDATORS: FieldValidator[] = [
  {
    validate: (plan: IMap<string, any>) => {
      const isSwlCoverageAdded = plan
        ?.get('coverageDefs')
        ?.some(cov => cov?.get('code') === GPLUCoverageCode.SPOUSE_WHOLE_LIFE);

      if (isSwlCoverageAdded) {
        const isValid = plan?.get('spouseCoveredOption')?.size > 0;
        return buildValidationResult(
          GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.spouseCoveredOption,
          isValid,
          "At least one option must be selected for 'Who is Covered?' under Spouse Whole Life"
        );
      }
    },
  },
];

const getMinMaxValidators = (minMaxConfig: Record<string, EntityMinMaxConfig>) => {
  const getMinMaxValidatorResult = createMinMaxValidatorsByProduct(A360ProductModel.GPLUMaster, minMaxConfig);

  return {
    WholeLife: [
      {
        validate: (coverage: IMap<string, any>) => {
          return getMinMaxValidatorResult(
            GPLU_WHOLE_LIFE_FIELD_ID.guaranteedIssueAmount,
            'GI Amount',
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount')
          );
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
            return getMinMaxValidatorResult(
              GPLU_WHOLE_LIFE_FIELD_ID.benefitMaxAmount,
              'Maximum Benefit Amount',
              getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMaxAmount.amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
            return getMinMaxValidatorResult(
              GPLU_WHOLE_LIFE_FIELD_ID.benefitMinAmount,
              'Minimum Benefit Amount',
              getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMinAmount.amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
            return getMinMaxValidatorResult(
              GPLU_WHOLE_LIFE_FIELD_ID.incrementAmount,
              'Increment Amount',
              getByKeyPath(coverage, 'benefitSchedule.rangeValues.incrementAmount.amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          if (isDefined(getByKeyPath(coverage, 'benefitSchedule.singleValue'))) {
            return getMinMaxValidatorResult(
              GPLU_WHOLE_LIFE_FIELD_ID.flatBenefitAmount,
              'Flat Benefit Amount',
              getByKeyPath(coverage, 'benefitSchedule.singleValue.flatBenefitAmount.amount')
            );
          }
        },
      },
    ],
    SpouseWholeLife: [
      {
        validate: (coverage: IMap<string, any>) => {
          const wsUwAmount = getSpouseUWAmount(coverage, GPLU_SPOUSE_TYPE.WS);
          if (isDefined(wsUwAmount)) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.guaranteedIssueAmount,
              'GI Amount',
              wsUwAmount
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
            coverage,
            GPLU_SPOUSE_TYPE.WS,
            'rangeValues'
          );

          if (benefitTypeValue) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.benefitMaxAmount,
              'Maximum Benefit Amount',
              getByKeyPath(benefitTypeValue, 'benefitMaxAmount.amount') ??
                benefitTypeValue?.benefitMaxAmount?.get('amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
            coverage,
            GPLU_SPOUSE_TYPE.WS,
            'rangeValues'
          );

          if (benefitTypeValue) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.benefitMinAmount,
              'Minimum Benefit Amount',
              getByKeyPath(benefitTypeValue, 'benefitMinAmount.amount') ??
                benefitTypeValue?.benefitMinAmount?.get('amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
            coverage,
            GPLU_SPOUSE_TYPE.WS,
            'rangeValues'
          );

          if (benefitTypeValue) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.incrementAmount,
              'Increment Amount',
              getByKeyPath(benefitTypeValue, 'incrementAmount.amount') ??
                benefitTypeValue?.incrementAmount?.get('amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
            coverage,
            GPLU_SPOUSE_TYPE.WS,
            'singleValue'
          );

          if (benefitTypeValue) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS.flatBenefitAmount,
              'Flat Benefit Amount',
              getByKeyPath(benefitTypeValue, 'flatBenefitAmount.amount') ?? benefitTypeValue?.flatBenefitAmount?.amount
            );
          }
        },
      },
      // NWS
      {
        validate: (coverage: IMap<string, any>) => {
          const nwsUwAmount = getSpouseUWAmount(coverage, GPLU_SPOUSE_TYPE.NWS);
          if (isDefined(nwsUwAmount)) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.guaranteedIssueAmount,
              'GI Amount',
              nwsUwAmount
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
            coverage,
            GPLU_SPOUSE_TYPE.NWS,
            'rangeValues'
          );

          if (benefitTypeValue) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.benefitMaxAmount,
              'Maximum Benefit Amount',
              getByKeyPath(benefitTypeValue, 'benefitMaxAmount.amount') ??
                benefitTypeValue?.benefitMaxAmount?.get('amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
            coverage,
            GPLU_SPOUSE_TYPE.NWS,
            'rangeValues'
          );
          if (benefitTypeValue) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.benefitMinAmount,
              'Minimum Benefit Amount',
              getByKeyPath(benefitTypeValue, 'benefitMinAmount.amount') ??
                benefitTypeValue?.benefitMinAmount?.get('amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
            coverage,
            GPLU_SPOUSE_TYPE.NWS,
            'rangeValues'
          );

          if (benefitTypeValue) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.incrementAmount,
              'Increment Amount',
              getByKeyPath(benefitTypeValue, 'incrementAmount.amount') ??
                benefitTypeValue?.incrementAmount?.get('amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          const {benefitTypeValue} = getSpouseUwAmountAndBenefitTypeValues(
            coverage,
            GPLU_SPOUSE_TYPE.NWS,
            'singleValue'
          );
          if (benefitTypeValue) {
            return getMinMaxValidatorResult(
              GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS.flatBenefitAmount,
              'Flat Benefit Amount',
              getByKeyPath(benefitTypeValue, 'flatBenefitAmount.amount') ?? benefitTypeValue?.flatBenefitAmount?.amount
            );
          }
        },
      },
    ],
    ChildTermLife: [
      {
        validate: (coverage: IMap<string, any>) => {
          return getMinMaxValidatorResult(
            GPLU_CHILD_TERM_LIFE_FIELD_ID.guaranteedIssueAmount,
            'GI Amount',
            getByKeyPath(coverage, 'underwriting.guaranteedIssueAmount.amount')
          );
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
            return getMinMaxValidatorResult(
              GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitMaxAmount,
              'Maximum Benefit Amount',
              getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMaxAmount.amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
            return getMinMaxValidatorResult(
              GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitMinAmount,
              'Minimum Benefit Amount',
              getByKeyPath(coverage, 'benefitSchedule.rangeValues.benefitMinAmount.amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          if (isDefined(getByKeyPath(coverage, 'benefitSchedule.rangeValues'))) {
            return getMinMaxValidatorResult(
              GPLU_CHILD_TERM_LIFE_FIELD_ID.incrementAmount,
              'Increment Amount',
              getByKeyPath(coverage, 'benefitSchedule.rangeValues.incrementAmount.amount')
            );
          }
        },
      },
      {
        validate: (coverage: IMap<string, any>) => {
          if (isDefined(getByKeyPath(coverage, 'benefitSchedule.singleValue'))) {
            return getMinMaxValidatorResult(
              GPLU_CHILD_TERM_LIFE_FIELD_ID.flatBenefitAmount,
              'Flat Benefit Amount',
              getByKeyPath(coverage, 'benefitSchedule.singleValue.flatBenefitAmount.amount')
            );
          }
        },
      },
    ],
  };
};

const getSpouseUwAmountAndBenefitTypeValues = (
  coverage: IMap<string, any>,
  spouseType: string,
  benefitTypeKey: string
) => {
  const benefitSchedules = getByKeyPath(coverage, 'benefitSchedules') as IList<IMap<string, any>>;

  const spouseUwAmount = getSpouseUWAmount(coverage, spouseType);
  const benefitScheduleType = benefitSchedules?.find(bs => bs?.get('spouseType') === spouseType);
  const benefitTypeValue = benefitScheduleType?.get(benefitTypeKey);
  return {
    spouseUwAmount,
    benefitTypeValue,
    benefitScheduleType,
  };
};

const getSpouseUWAmount = (coverage: IMap<string, any>, spouseType: string) => {
  const spouseUwType =
    spouseType === GPLU_SPOUSE_TYPE.WS ? 'GPLUSpouseUnderwritingAmountsWs' : 'GPLUSpouseUnderwritingAmountsNws';

  const spouseUwAmountByType = (getByKeyPath(coverage, 'underwriting.spouseUnderwritingAmounts') as IList<any>)?.find(
    uwAmount => uwAmount?.get('_type') === spouseUwType
  );

  return getByKeyPath(spouseUwAmountByType, 'guaranteedIssueAmount.amount') as number;
};

export const getGpluValidators = (minMaxConfig: Record<string, EntityMinMaxConfig>) => ({
  coverageFieldValidators: GPLU_COVERAGE_FIELD_VALIDATORS,
  planFieldValidators: GPLU_PLAN_FIELD_VALIDATORS,
  quoteFieldValidators: GPLU_QUOTE_FIELDS_VALIDATORS,
  minMaxValidators: getMinMaxValidators(minMaxConfig),
});
